import type { CSSProperties, ReactNode } from 'react';

/**
 * よりそいノート公式マスコット（インラインSVG版）
 *
 * Comfy/PNG差し替え用メモ:
 * - 各 MascotId はそのままファイル名に対応させる（例: public/mascots/momo.png）
 * - PNG化する場合は Mascot 内の <svg> 描画を <img src={`/mascots/${id}.png`}> に置き換えるだけでよい
 * - viewBox は全キャラ共通 120x120、透過背景
 */

export type MascotId =
  | 'momo'
  | 'haha'
  | 'papa'
  | 'nurse'
  | 'shop'
  | 'heart'
  | 'star'
  | 'sleep';

/** 全キャラ共通の線・ほっぺスタイル（サイトのインク色を柔らかく） */
const LINE = {
  stroke: '#43253A',
  strokeOpacity: 0.45,
  strokeWidth: 4,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
} as const;

const FACE = {
  fill: '#43253A',
  fillOpacity: 0.78,
} as const;

const CHEEK = {
  fill: '#FF9EBB',
  fillOpacity: 0.55,
} as const;

/* ---------------------------------- Momo ---------------------------------- */
/** もも — ピーチピンクのまんまる赤ちゃん（ヒーローの相棒） */
function MomoArt() {
  return (
    <g>
      {/* 頭のちょん毛 */}
      <path
        d="M60 24 C60 15 68 11 73 15 C70 17 68 21 69 25"
        fill="none"
        {...LINE}
      />
      {/* からだ（ちいさい） */}
      <ellipse cx={60} cy={103} rx={18} ry={11} fill="#FFD9E2" {...LINE} />
      {/* おてて */}
      <circle cx={21} cy={66} r={6.5} fill="#FFC2D1" {...LINE} />
      <circle cx={99} cy={66} r={6.5} fill="#FFC2D1" {...LINE} />
      {/* 大きなあたま */}
      <circle cx={60} cy={60} r={40} fill="#FFC2D1" {...LINE} />
      {/* 目 */}
      <circle cx={46} cy={56} r={4.2} {...FACE} />
      <circle cx={74} cy={56} r={4.2} {...FACE} />
      {/* ほっぺ */}
      <ellipse cx={36} cy={68} rx={7} ry={4.5} {...CHEEK} />
      <ellipse cx={84} cy={68} rx={7} ry={4.5} {...CHEEK} />
      {/* 口（にっこり） */}
      <path
        d="M53 66 Q60 73 67 66"
        fill="none"
        stroke={LINE.stroke}
        strokeOpacity={0.55}
        strokeWidth={3.5}
        strokeLinecap="round"
      />
    </g>
  );
}

/* ---------------------------------- Haha ---------------------------------- */
/** はは — やさしいママうさぎ（ふわふわ耳） */
function HahaArt() {
  return (
    <g>
      {/* 耳 */}
      <ellipse
        cx={44}
        cy={22}
        rx={10}
        ry={20}
        fill="#FFF5F8"
        {...LINE}
        transform="rotate(-10 44 22)"
      />
      <ellipse
        cx={76}
        cy={22}
        rx={10}
        ry={20}
        fill="#FFF5F8"
        {...LINE}
        transform="rotate(10 76 22)"
      />
      <ellipse
        cx={44}
        cy={25}
        rx={4.5}
        ry={12}
        fill="#FFC2D1"
        fillOpacity={0.7}
        transform="rotate(-10 44 25)"
      />
      <ellipse
        cx={76}
        cy={25}
        rx={4.5}
        ry={12}
        fill="#FFC2D1"
        fillOpacity={0.7}
        transform="rotate(10 76 25)"
      />
      {/* からだ */}
      <ellipse cx={60} cy={103} rx={17} ry={10} fill="#FFF5F8" {...LINE} />
      {/* あたま */}
      <circle cx={60} cy={64} r={36} fill="#FFF5F8" {...LINE} />
      {/* 目（やさしい垂れ目） */}
      <circle cx={47} cy={61} r={4} {...FACE} />
      <circle cx={73} cy={61} r={4} {...FACE} />
      {/* まつげ */}
      <path
        d="M41 55 L38 52 M79 55 L82 52"
        fill="none"
        stroke={LINE.stroke}
        strokeOpacity={0.45}
        strokeWidth={2.5}
        strokeLinecap="round"
      />
      {/* ほっぺ */}
      <ellipse cx={38} cy={71} rx={6.5} ry={4.2} {...CHEEK} />
      <ellipse cx={82} cy={71} rx={6.5} ry={4.2} {...CHEEK} />
      {/* 口 */}
      <path
        d="M54 70 Q60 76 66 70"
        fill="none"
        stroke={LINE.stroke}
        strokeOpacity={0.55}
        strokeWidth={3.5}
        strokeLinecap="round"
      />
      {/* 胸のハート */}
      <path
        d="M60 97 C58 94 54 94 54 97.5 C54 100.5 60 103.5 60 103.5 C60 103.5 66 100.5 66 97.5 C66 94 62 94 60 97 Z"
        fill="#FF8FAB"
        fillOpacity={0.85}
      />
    </g>
  );
}

/* ---------------------------------- Papa ---------------------------------- */
/** ぱぱ — シンプルなパパくま（パートナーセクション） */
function PapaArt() {
  return (
    <g>
      {/* 耳 */}
      <circle cx={31} cy={33} r={12} fill="#EFD8C2" {...LINE} />
      <circle cx={89} cy={33} r={12} fill="#EFD8C2" {...LINE} />
      <circle cx={31} cy={33} r={5} fill="#FFF8F3" fillOpacity={0.8} />
      <circle cx={89} cy={33} r={5} fill="#FFF8F3" fillOpacity={0.8} />
      {/* からだ */}
      <ellipse cx={60} cy={103} rx={19} ry={11} fill="#EFD8C2" {...LINE} />
      {/* 蝶ネクタイ */}
      <path
        d="M60 92 L52 87 L52 97 Z M60 92 L68 87 L68 97 Z"
        fill="#F45D8F"
        fillOpacity={0.85}
      />
      <circle cx={60} cy={92} r={3} fill="#E23E77" />
      {/* あたま */}
      <circle cx={60} cy={61} r={38} fill="#EFD8C2" {...LINE} />
      {/* マズル */}
      <ellipse cx={60} cy={72} rx={13} ry={9.5} fill="#FFF8F3" />
      {/* 鼻・口 */}
      <ellipse cx={60} cy={68} rx={3.6} ry={2.8} {...FACE} />
      <path
        d="M60 71.5 Q60 75 56 76 M60 71.5 Q60 75 64 76"
        fill="none"
        stroke={LINE.stroke}
        strokeOpacity={0.55}
        strokeWidth={2.8}
        strokeLinecap="round"
      />
      {/* 目 */}
      <circle cx={45} cy={55} r={4.2} {...FACE} />
      <circle cx={75} cy={55} r={4.2} {...FACE} />
      {/* ほっぺ */}
      <ellipse cx={36} cy={66} rx={6.5} ry={4.2} {...CHEEK} />
      <ellipse cx={84} cy={66} rx={6.5} ry={4.2} {...CHEEK} />
    </g>
  );
}

/* ---------------------------------- Nurse ---------------------------------- */
/** ナース — 小さなクリニックの助っ人（病院・手続き） */
function NurseArt() {
  return (
    <g>
      {/* からだ */}
      <ellipse cx={60} cy={103} rx={16} ry={10} fill="#C8F0E0" {...LINE} />
      {/* あたま */}
      <circle cx={60} cy={62} r={34} fill="#FFF8F3" {...LINE} />
      {/* ナース帽 */}
      <rect
        x={42}
        y={14}
        width={36}
        height={17}
        rx={7}
        fill="#ffffff"
        {...LINE}
      />
      <rect x={57.2} y={17.5} width={5.6} height={10} rx={1.5} fill="#F45D8F" />
      <rect x={54.5} y={20.2} width={11} height={4.6} rx={1.5} fill="#F45D8F" />
      {/* 目（きらきら） */}
      <circle cx={47} cy={60} r={4} {...FACE} />
      <circle cx={73} cy={60} r={4} {...FACE} />
      <circle cx={48.4} cy={58.6} r={1.3} fill="#ffffff" fillOpacity={0.95} />
      <circle cx={74.4} cy={58.6} r={1.3} fill="#ffffff" fillOpacity={0.95} />
      {/* ほっぺ */}
      <ellipse cx={38} cy={70} rx={6} ry={4} {...CHEEK} />
      <ellipse cx={82} cy={70} rx={6} ry={4} {...CHEEK} />
      {/* 口 */}
      <path
        d="M54 69 Q60 75 66 69"
        fill="none"
        stroke={LINE.stroke}
        strokeOpacity={0.55}
        strokeWidth={3.5}
        strokeLinecap="round"
      />
    </g>
  );
}

/* ---------------------------------- Shop ---------------------------------- */
/** ショップ — おかいものバッグうさぎ */
function ShopArt() {
  return (
    <g>
      {/* バッグの持ち手 */}
      <path
        d="M46 60 C46 46 74 46 74 60"
        fill="none"
        {...LINE}
      />
      {/* バッグ本体（ちいさなからだ） */}
      <rect
        x={30}
        y={58}
        width={60}
        height={50}
        rx={14}
        fill="#FF8FAB"
        {...LINE}
      />
      {/* バッグのハート */}
      <path
        d="M60 92 C58 89 53 89 53 93 C53 96.5 60 100 60 100 C60 100 67 96.5 67 93 C67 89 62 89 60 92 Z"
        fill="#ffffff"
        fillOpacity={0.9}
      />
      {/* うさぎの耳 */}
      <ellipse
        cx={48}
        cy={28}
        rx={7}
        ry={15}
        fill="#FFF5F8"
        {...LINE}
        transform="rotate(-8 48 28)"
      />
      <ellipse
        cx={72}
        cy={28}
        rx={7}
        ry={15}
        fill="#FFF5F8"
        {...LINE}
        transform="rotate(8 72 28)"
      />
      {/* うさぎの顔 */}
      <circle cx={60} cy={46} r={21} fill="#FFF5F8" {...LINE} />
      <circle cx={52.5} cy={45} r={3.2} {...FACE} />
      <circle cx={67.5} cy={45} r={3.2} {...FACE} />
      <ellipse cx={47} cy={52} rx={4.5} ry={3} {...CHEEK} />
      <ellipse cx={73} cy={52} rx={4.5} ry={3} {...CHEEK} />
      <path
        d="M56 51 Q60 55 64 51"
        fill="none"
        stroke={LINE.stroke}
        strokeOpacity={0.55}
        strokeWidth={3}
        strokeLinecap="round"
      />
    </g>
  );
}

/* ---------------------------------- Heart ---------------------------------- */
/** ハート — こころカードのハートのかたまり */
function HeartArt() {
  return (
    <g>
      {/* ハートのからだ */}
      <path
        d="M60 104 C40 88 20 72 20 50 C20 34 33 26 45 26 C53 26 58 31 60 35 C62 31 67 26 75 26 C87 26 100 34 100 50 C100 72 80 88 60 104 Z"
        fill="#FF8FAB"
        {...LINE}
      />
      {/* おてて */}
      <circle cx={20} cy={66} r={6} fill="#FF8FAB" {...LINE} />
      <circle cx={100} cy={66} r={6} fill="#FF8FAB" {...LINE} />
      {/* 目 */}
      <circle cx={47} cy={55} r={4.2} {...FACE} />
      <circle cx={73} cy={55} r={4.2} {...FACE} />
      <circle cx={48.5} cy={53.5} r={1.4} fill="#ffffff" fillOpacity={0.95} />
      <circle cx={74.5} cy={53.5} r={1.4} fill="#ffffff" fillOpacity={0.95} />
      {/* ほっぺ */}
      <ellipse cx={36} cy={65} rx={6.5} ry={4.2} fill="#E23E77" fillOpacity={0.35} />
      <ellipse cx={84} cy={65} rx={6.5} ry={4.2} fill="#E23E77" fillOpacity={0.35} />
      {/* 口 */}
      <path
        d="M53 64 Q60 71 67 64"
        fill="none"
        stroke={LINE.stroke}
        strokeOpacity={0.55}
        strokeWidth={3.5}
        strokeLinecap="round"
      />
    </g>
  );
}

/* ---------------------------------- Star ---------------------------------- */
/** スター — ヒント・進捗のきらきら */
function StarArt() {
  return (
    <g>
      {/* まわりのミニきらきら */}
      <path
        d="M26 22 L28.5 28.5 L35 31 L28.5 33.5 L26 40 L23.5 33.5 L17 31 L23.5 28.5 Z"
        fill="#E7D6FF"
        {...LINE}
      />
      <path
        d="M95 15 L97 20 L102 22 L97 24 L95 29 L93 24 L88 22 L93 20 Z"
        fill="#C8F0E0"
        {...LINE}
      />
      {/* あし */}
      <ellipse cx={52} cy={103} rx={7} ry={5} fill="#FFD98E" {...LINE} />
      <ellipse cx={68} cy={103} rx={7} ry={5} fill="#FFD98E" {...LINE} />
      {/* 星のからだ */}
      <path
        d="M60 20 C63 34 66 39 82 43 C94 46 94 51 84 57 C72 64 70 69 70 84 C70 96 65 99 60 90 C55 99 50 96 50 84 C50 69 48 64 36 57 C26 51 26 46 38 43 C54 39 57 34 60 20 Z"
        fill="#FFE29A"
        {...LINE}
      />
      {/* 目（うれしそうな曲線） */}
      <path
        d="M43 58 Q47 53 51 58 M69 58 Q73 53 77 58"
        fill="none"
        stroke={LINE.stroke}
        strokeOpacity={0.6}
        strokeWidth={3.5}
        strokeLinecap="round"
      />
      {/* ほっぺ */}
      <ellipse cx={42} cy={66} rx={6} ry={4} {...CHEEK} />
      <ellipse cx={78} cy={66} rx={6} ry={4} {...CHEEK} />
      {/* 口（わーい） */}
      <path
        d="M54 63 Q60 72 66 63"
        fill="#F45D8F"
        fillOpacity={0.5}
        stroke={LINE.stroke}
        strokeOpacity={0.55}
        strokeWidth={3}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </g>
  );
}

/* ---------------------------------- Sleep ---------------------------------- */
/** スリープ — ねむねむ月の赤ちゃん（産後） */
function SleepArt() {
  return (
    <g>
      {/* Z z */}
      <text
        x={80}
        y={26}
        fontSize={15}
        fontWeight={700}
        fill="#B9A3E8"
        stroke={LINE.stroke}
        strokeOpacity={0.35}
        strokeWidth={0.8}
        transform="rotate(8 80 26)"
      >
        Z
      </text>
      <text
        x={93}
        y={42}
        fontSize={11}
        fontWeight={700}
        fill="#B9A3E8"
        transform="rotate(12 93 42)"
      >
        z
      </text>
      {/* 三日月 */}
      <path
        d="M74 18 C48 24 30 46 30 72 C30 96 48 110 68 110 C82 110 92 102 97 92 C88 97 77 99 68 96 C50 90 40 72 45 53 C50 36 61 25 74 18 Z"
        fill="#E7D6FF"
        {...LINE}
      />
      {/* お星さま帽子の先 */}
      <path
        d="M97 92 L99.5 97.5 L105 99.5 L99.5 101.5 L97 107 L94.5 101.5 L89 99.5 L94.5 97.5 Z"
        fill="#FFE29A"
        {...LINE}
      />
      {/* 閉じた目（すやすや） */}
      <path
        d="M48 66 Q53 70 58 66 M68 70 Q73 74 78 70"
        fill="none"
        stroke={LINE.stroke}
        strokeOpacity={0.6}
        strokeWidth={3.5}
        strokeLinecap="round"
      />
      {/* ほっぺ */}
      <ellipse cx={46} cy={75} rx={5.5} ry={3.8} {...CHEEK} />
      <ellipse cx={82} cy={81} rx={5.5} ry={3.8} {...CHEEK} />
      {/* 小さな口 */}
      <circle cx={64} cy={76} r={2.6} fill="#F45D8F" fillOpacity={0.55} />
    </g>
  );
}

/* ------------------------------ レジストリ ------------------------------ */

const mascotArt: Record<MascotId, () => ReactNode> = {
  momo: MomoArt,
  haha: HahaArt,
  papa: PapaArt,
  nurse: NurseArt,
  shop: ShopArt,
  heart: HeartArt,
  star: StarArt,
  sleep: SleepArt,
};

export const mascotNames: Record<MascotId, string> = {
  momo: 'もも',
  haha: 'はは',
  papa: 'ぱぱ',
  nurse: 'ナース',
  shop: 'ショップ',
  heart: 'ハート',
  star: 'スター',
  sleep: 'スリープ',
};

/** ステージ → マスコットの対応（PNG化後も同じキーを使う） */
export const stageMascots: Record<string, MascotId> = {
  'pregnancy-1': 'momo',
  'pregnancy-2': 'star',
  'pregnancy-3': 'shop',
  birth: 'nurse',
  'postpartum-early': 'sleep',
  'postpartum-late': 'haha',
};

/* ------------------------------ Mascot wrapper ------------------------------ */

interface MascotProps {
  id: MascotId;
  /** 表示サイズ(px)。正方形で描画される */
  size?: number;
  /** 意味を持つ画像にしたいときのタイトル。省略時は装飾扱い */
  title?: string;
  className?: string;
  style?: CSSProperties;
}

export function Mascot({ id, size = 64, title, className, style }: MascotProps) {
  const art = mascotArt[id];
  return (
    <svg
      viewBox="0 0 120 120"
      width={size}
      height={size}
      className={className}
      style={style}
      role={title ? 'img' : undefined}
      aria-hidden={title ? undefined : true}
      focusable="false"
    >
      {title && <title>{title}</title>}
      {art()}
    </svg>
  );
}
